import React, { useEffect, useMemo, useRef } from 'react';
import { useCookies } from 'react-cookie';
import { COOKIE_CONSTANTS } from '../../constants/CookieConsent';
import './cookieConsentBanner.scss';

const isBrowser = typeof window !== 'undefined';

type CookieConsentChoice = typeof COOKIE_CONSTANTS.GRANTED | typeof COOKIE_CONSTANTS.DENIED;

// Local cookie options typing (structurally compatible with react-cookie options)
type CookieSetOptions = {
  path?: string;
  domain?: string;
  expires?: Date;
  maxAge?: number;
  secure?: boolean;
  sameSite?: 'lax' | 'strict' | 'none';
};

export const buildConsentCookieOptions = (): CookieSetOptions => ({
  path: '/',
  sameSite: 'lax',
  secure: isBrowser ? window.location.protocol === 'https:' : false,
  maxAge: 60 * 60 * 24 * 365 // 1 year
})

const CookieConsentBanner = () => {
  const [cookies, setCookie] = useCookies([COOKIE_CONSTANTS.COOKIE_NAME]);
  const consentChoice = cookies[COOKIE_CONSTANTS.COOKIE_NAME] as CookieConsentChoice | undefined;
  const cookieOptions = useMemo(() => buildConsentCookieOptions(), []);
  const shouldDisplayBanner =
    consentChoice !== COOKIE_CONSTANTS.GRANTED && consentChoice !== COOKIE_CONSTANTS.DENIED;

  const hasSentClarityRef = useRef(false);

  const sendClarityConsent = (cookieAction: CookieConsentChoice) => {
    hasSentClarityRef.current = true;
    //@ts-ignore
    if (typeof window !== 'undefined' && typeof window.clarity === 'function') {
      //@ts-ignore
      window.clarity('consentv2', {
        ad_Storage: COOKIE_CONSTANTS.DENIED,
        analytics_Storage: cookieAction,
      });
    }
  };

  useEffect(() => {
    if (hasSentClarityRef.current) return;
    if (consentChoice !== COOKIE_CONSTANTS.GRANTED && consentChoice !== COOKIE_CONSTANTS.DENIED) return;

    const timeoutId = window.setTimeout(() => {
      sendClarityConsent(consentChoice);
    }, 2000);

    return () => window.clearTimeout(timeoutId);
  }, [consentChoice]);

  const handleCookieConsent = (cookieAction: CookieConsentChoice) => {
    setCookie(COOKIE_CONSTANTS.COOKIE_NAME, cookieAction, cookieOptions);
    sendClarityConsent(cookieAction);
  };

  if (!shouldDisplayBanner) return null;

  return (
    <div className="cookieConsentContainer">
      <div className="contentContainer">
        <span className="attentionText">Attention:</span>
        {COOKIE_CONSTANTS.MESSAGE}
      </div>
      <div className="cookieBtnWrapper">
        <button
          id="declineButton"
          className="declineButton"
          onClick={() => handleCookieConsent(COOKIE_CONSTANTS.DENIED)}
        >
          {COOKIE_CONSTANTS.DECLINE_BUTTON_TEXT}
        </button>
        <button
          id="acceptButton"
          onClick={() => handleCookieConsent(COOKIE_CONSTANTS.GRANTED)}
          className="acceptButton"
        >
          {COOKIE_CONSTANTS.ACCEPT_BUTTON_TEXT}
        </button>
      </div>
    </div>
  );
};

export default CookieConsentBanner;
